import { Component, OnInit, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { FinanceApiService, GeographicAreaRecord } from '../../features/finance/finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-geographic-route-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GeographicRouteSelectorComponent),
      multi: true
    }
  ],
  template: `
    <div class="grid">
      <label>
        <span>{{ t('platform.geography.fields.country') }}</span>
        <p-select
          [options]="countryOptions()"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="selection.countryAreaId"
          [showClear]="true"
          appendTo="body"
          (ngModelChange)="onCountryChanged()" />
      </label>
      <label>
        <span>{{ t('platform.geography.fields.province') }}</span>
        <p-select
          [options]="provinceOptions()"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="selection.provinceAreaId"
          [disabled]="!selection.countryAreaId"
          [showClear]="true"
          appendTo="body"
          (ngModelChange)="onProvinceChanged()" />
      </label>
      <label>
        <span>{{ t('platform.geography.fields.city') }}</span>
        <p-select
          [options]="cityOptions()"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="selection.cityAreaId"
          [disabled]="!selection.provinceAreaId"
          [showClear]="true"
          appendTo="body"
          (ngModelChange)="onCityChanged()" />
      </label>
      <label>
        <span>{{ t('platform.geography.fields.area') }}</span>
        <p-select
          [options]="districtOptions()"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="selection.districtAreaId"
          [disabled]="!selection.cityAreaId"
          [showClear]="true"
          appendTo="body"
          (ngModelChange)="onDistrictChanged()" />
      </label>
      <label>
        <span>{{ t('platform.geography.fields.route') }}</span>
        <p-select
          [options]="routeOptions()"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="selection.routeAreaId"
          [disabled]="!selection.cityAreaId"
          [showClear]="true"
          appendTo="body"
          (ngModelChange)="onRouteChanged()" />
      </label>
    </div>
    @if (breadcrumb()) {
      <p class="breadcrumb">{{ breadcrumb() }}</p>
    }
  `,
  styles: [`
    .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .breadcrumb { margin: 8px 0 0; padding: 8px 10px; border-radius: 8px; background: #f1f5f9; color: #334155; font-size: 13px; }
    @media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class GeographicRouteSelectorComponent implements ControlValueAccessor, OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);

  readonly countryOptions = signal<{ label: string; value: string }[]>([]);
  readonly provinceOptions = signal<{ label: string; value: string }[]>([]);
  readonly cityOptions = signal<{ label: string; value: string }[]>([]);
  readonly districtOptions = signal<{ label: string; value: string }[]>([]);
  readonly routeOptions = signal<{ label: string; value: string }[]>([]);
  readonly breadcrumb = signal('');

  selection = {
    countryAreaId: null as string | null,
    provinceAreaId: null as string | null,
    cityAreaId: null as string | null,
    districtAreaId: null as string | null,
    routeAreaId: null as string | null
  };

  private areaById = new Map<string, GeographicAreaRecord>();
  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private hydrateToken = 0;

  ngOnInit(): void {
    this.api.listGeographicAreas().subscribe({
      next: rows => {
        this.cacheAreas(rows);
        this.countryOptions.set(this.toOptions(rows));
      },
      error: () => this.countryOptions.set([])
    });
  }

  writeValue(routeAreaId: string | null): void {
    const normalized = routeAreaId?.trim() || null;
    this.selection = {
      countryAreaId: null,
      provinceAreaId: null,
      cityAreaId: null,
      districtAreaId: null,
      routeAreaId: normalized
    };
    this.breadcrumb.set('');
    if (normalized) {
      this.hydrateFromRouteId(normalized);
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
    // individual selects use template bindings when needed
  }

  onCountryChanged(): void {
    this.selection.provinceAreaId = null;
    this.selection.cityAreaId = null;
    this.selection.districtAreaId = null;
    this.selection.routeAreaId = null;
    this.provinceOptions.set([]);
    this.cityOptions.set([]);
    this.districtOptions.set([]);
    this.routeOptions.set([]);
    this.breadcrumb.set('');
    if (this.selection.countryAreaId) {
      this.loadChildren(this.selection.countryAreaId, rows => this.provinceOptions.set(this.toOptions(rows)));
    }
    this.emitValue();
  }

  onProvinceChanged(): void {
    this.selection.cityAreaId = null;
    this.selection.districtAreaId = null;
    this.selection.routeAreaId = null;
    this.cityOptions.set([]);
    this.districtOptions.set([]);
    this.routeOptions.set([]);
    this.breadcrumb.set('');
    if (this.selection.provinceAreaId) {
      this.loadChildren(this.selection.provinceAreaId, rows => this.cityOptions.set(this.toOptions(rows)));
    }
    this.emitValue();
  }

  onCityChanged(): void {
    this.selection.districtAreaId = null;
    this.selection.routeAreaId = null;
    this.districtOptions.set([]);
    this.routeOptions.set([]);
    this.breadcrumb.set('');
    if (this.selection.cityAreaId) {
      this.loadChildren(this.selection.cityAreaId, rows => this.districtOptions.set(this.toOptions(rows)));
    }
    this.emitValue();
  }

  onDistrictChanged(): void {
    this.selection.routeAreaId = null;
    this.routeOptions.set([]);
    this.breadcrumb.set('');
    const parentId = this.selection.districtAreaId ?? this.selection.cityAreaId;
    if (parentId) {
      this.loadChildren(parentId, rows => this.routeOptions.set(this.toOptions(rows)));
    }
    this.emitValue();
  }

  onRouteChanged(): void {
    this.updateBreadcrumb();
    this.emitValue();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private emitValue(): void {
    this.onTouched();
    this.onChange(this.selection.routeAreaId);
  }

  private loadChildren(parentId: string, apply: (rows: GeographicAreaRecord[]) => void): void {
    this.api.listGeographicAreas({ parentAreaId: parentId }).subscribe({
      next: rows => {
        this.cacheAreas(rows);
        apply(rows);
      },
      error: () => apply([])
    });
  }

  private hydrateFromRouteId(routeAreaId: string): void {
    const token = ++this.hydrateToken;
    this.api.getGeographicArea(routeAreaId).subscribe({
      next: route => {
        if (token !== this.hydrateToken) {
          return;
        }

        this.cacheAreas([route]);
        this.buildChain(route).then(chain => {
          if (token !== this.hydrateToken) {
            return;
          }

          this.applyChain(chain);
          this.updateBreadcrumb();
        });
      }
    });
  }

  private async buildChain(leaf: GeographicAreaRecord): Promise<GeographicAreaRecord[]> {
    const chain: GeographicAreaRecord[] = [leaf];
    let parentId = leaf.parentAreaId;
    while (parentId) {
      let parent = this.areaById.get(parentId);
      if (!parent) {
        parent = await new Promise<GeographicAreaRecord>((resolve, reject) => {
          this.api.getGeographicArea(parentId!).subscribe({
            next: resolve,
            error: () => reject()
          });
        });
        this.cacheAreas([parent]);
      }

      chain.unshift(parent);
      parentId = parent.parentAreaId;
    }

    return chain;
  }

  private applyChain(chain: GeographicAreaRecord[]): void {
    for (const area of chain) {
      switch (area.level) {
        case 'Country':
          this.selection.countryAreaId = area.id;
          break;
        case 'Province':
          this.selection.provinceAreaId = area.id;
          break;
        case 'City':
          this.selection.cityAreaId = area.id;
          break;
        case 'Area':
          this.selection.districtAreaId = area.id;
          break;
        case 'Route':
          this.selection.routeAreaId = area.id;
          break;
      }
    }

    if (this.selection.countryAreaId) {
      this.loadChildren(this.selection.countryAreaId, rows => this.provinceOptions.set(this.toOptions(rows)));
    }
    if (this.selection.provinceAreaId) {
      this.loadChildren(this.selection.provinceAreaId, rows => this.cityOptions.set(this.toOptions(rows)));
    }
    if (this.selection.cityAreaId) {
      this.loadChildren(this.selection.cityAreaId, rows => this.districtOptions.set(this.toOptions(rows)));
    }
    const routeParent = this.selection.districtAreaId ?? this.selection.cityAreaId;
    if (routeParent) {
      this.loadChildren(routeParent, rows => this.routeOptions.set(this.toOptions(rows)));
    }
  }

  private updateBreadcrumb(): void {
    if (!this.selection.routeAreaId) {
      this.breadcrumb.set('');
      return;
    }

    const route = this.areaById.get(this.selection.routeAreaId);
    if (!route) {
      this.api.getGeographicArea(this.selection.routeAreaId).subscribe({
        next: area => {
          this.cacheAreas([area]);
          this.breadcrumb.set(this.formatBreadcrumbFromSelection());
        }
      });
      return;
    }

    this.breadcrumb.set(this.formatBreadcrumbFromSelection());
  }

  private formatBreadcrumbFromSelection(): string {
    const parts: string[] = [];
    const add = (id: string | null) => {
      if (!id) {
        return;
      }

      const area = this.areaById.get(id);
      if (area) {
        parts.push(area.name);
      }
    };

    add(this.selection.countryAreaId);
    add(this.selection.provinceAreaId);
    add(this.selection.cityAreaId);
    add(this.selection.districtAreaId);
    add(this.selection.routeAreaId);
    return parts.join(' › ');
  }

  private cacheAreas(rows: GeographicAreaRecord[]): void {
    for (const row of rows) {
      this.areaById.set(row.id, row);
    }
  }

  private toOptions(rows: GeographicAreaRecord[]): { label: string; value: string }[] {
    return rows
      .filter(item => item.isActive)
      .map(item => ({ label: `${item.code} — ${item.name}`, value: item.id }));
  }
}
